import { useEffect, useMemo, useState } from 'react';
import { X, Clock, MessageSquare, Paperclip, Link as LinkIcon, Plus, ExternalLink, User as UserIcon, Star, History, Calendar } from 'lucide-react';
import { Task, User, TaskStatus, TimeLog, Sprint, ChangeLog, Epic, Project, Team, Comment } from '../types';
import CostPerHourDisplay from './CostPerHourDisplay';
import { MarkdownDescription } from './MarkdownDescription';
import { RichDescriptionEditor } from './RichDescriptionEditor';
import { calculateEstimatedCost, calculateActualCost, formatCurrency } from '../utils/costUtils';
import { generateId } from '../utils/idGenerator';
import { canViewCost } from '../utils/permissions';
import { canEditTaskParameters, canViewProjectFinance, getProjectMemberRole, getProjectTeamUsers, isExternalPartnerUser } from '../utils/projectAccess';
import { parseDateLike } from '../utils/dateUtils';
import { ensureCostItemCode } from '../utils/costItems';
import { getTaskOrder } from '../utils/taskFiltering';
import { CommentSection } from './CommentSection';

interface TaskDetailsModalProps {
  task: Task;
  onClose: () => void;
  onUpdate: (taskId: string, updates: Partial<Task>) => void;
  users: User[];
  currentUser: User;
  existingTasks: Task[];
  sprints: Sprint[];
  teams?: Team[];
  epic?: Epic | null;
  project?: Project | null;
  onUpdateEpic?: (epicId: string, updates: Partial<Epic>) => void;
  onUpdateProject?: (projectId: string, updates: Partial<Project>) => void;
}

export default function TaskDetailsModal({
  task,
  onClose,
  onUpdate,
  users,
  currentUser,
  existingTasks,
  sprints,
  teams = [],
  epic,
  project,
  onUpdateEpic,
  onUpdateProject,
}: TaskDetailsModalProps) {
  const [activeTab, setActiveTab] = useState<'details' | 'comments' | 'standup' | 'time' | 'attachments' | 'changes'>('details');
  const [localComments, setLocalComments] = useState<Comment[]>(task.comments || []);
  const [standupNote, setStandupNote] = useState('');
  const [timeLog, setTimeLog] = useState({ hours: '', description: '' });
  const [verificationLink, setVerificationLink] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [attachmentName, setAttachmentName] = useState('');
  const [targetDate, setTargetDate] = useState(task.targetCompletionDate || '');
  const [nextUpdateDate, setNextUpdateDate] = useState(task.nextUpdateDate || '');
  const [startDate, setStartDate] = useState(task.startDate || '');
  const [endDate, setEndDate] = useState(task.targetEndDate || '');
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [selectedCostItemIds, setSelectedCostItemIds] = useState<string[]>(task.costItemIds || []);
  const [descriptionDraft, setDescriptionDraft] = useState(task.description || '');

  useEffect(() => {
    setDescriptionDraft(task.description || '');
  }, [task.id, task.description]);

  const formatDate = (value: unknown): string => {
    const parsedDate = parseDateLike(value);
    return parsedDate ? parsedDate.toLocaleDateString() : 'Unknown date';
  };

  const formatTime = (value: unknown): string => {
    const parsedDate = parseDateLike(value);
    return parsedDate ? parsedDate.toLocaleTimeString() : 'Unknown time';
  };

  const buildIdentitySet = (value?: string | null): Set<string> => {
    const identities = new Set<string>();
    const normalized = String(value || '').trim().toLowerCase();

    if (!normalized) {
      return identities;
    }

    identities.add(normalized);

    const withoutCommonPrefix = normalized.replace(/^user[-_]/, '');
    if (withoutCommonPrefix) {
      identities.add(withoutCommonPrefix);
    }

    const compact = normalized.replace(/[^a-z0-9]/g, '');
    if (compact) {
      identities.add(compact);
    }

    if (normalized.includes('@')) {
      const localPart = normalized.split('@')[0];
      if (localPart) {
        identities.add(localPart);
        identities.add(localPart.replace(/[^a-z0-9]/g, ''));
      }
    }

    return identities;
  };

  const userIdentitySet = (user: User): Set<string> => {
    const identities = new Set<string>();
    buildIdentitySet(user.id).forEach((value) => identities.add(value));
    buildIdentitySet(user.email).forEach((value) => identities.add(value));
    return identities;
  };

  const assignedIdentitySet = useMemo(() => {
    const assigned = new Set<string>();
    (task.assigneeIds || []).forEach((assigneeId) => {
      buildIdentitySet(assigneeId).forEach((value) => assigned.add(value));
    });
    return assigned;
  }, [task.assigneeIds]);

  const assignableUsers = useMemo(() => {
    return project ? getProjectTeamUsers(project, users, teams) : users;
  }, [project, users, teams]);

  const availableAssignees = assignableUsers.filter((user) => {
    const identities = userIdentitySet(user);
    return !Array.from(identities).some((identity) => assignedIdentitySet.has(identity));
  });
  const currentUserIdentitySet = useMemo(() => userIdentitySet(currentUser), [currentUser]);
  const isCurrentUserAssignee = useMemo(
    () => Array.from(currentUserIdentitySet).some((identity) => assignedIdentitySet.has(identity)),
    [currentUserIdentitySet, assignedIdentitySet]
  );
  const projectMemberRole = project
    ? getProjectMemberRole(project, currentUser.id, currentUser.email)
    : currentUser.role;
  const canEditTaskParams = project
    ? canEditTaskParameters(currentUser, project)
    : currentUser.role !== 'stakeholder';
  const canSetNextUpdateDate =
    canEditTaskParams || isCurrentUserAssignee || Boolean(projectMemberRole);
  const projectCostItems = project?.costs || [];
  const linkedCostItems = projectCostItems.filter((item) =>
    (selectedCostItemIds || []).includes(item.id)
  );
  const canViewFinance = project
    ? canViewProjectFinance(currentUser, project)
    : canViewCost(currentUser);
  const visibleChangeLogs = (task.changeLogs || []).filter((change) => {
    if (change.changeType !== 'cost') {
      return true;
    }

    if (!canViewFinance) {
      return false;
    }

    if (currentUser.role === 'admin') {
      return true;
    }

    const changedByUser = users.find((user) => user.id === change.changedBy);
    return !isExternalPartnerUser(changedByUser || null, project);
  });

  const assignees = users.filter((user) => {
    const identities = userIdentitySet(user);
    return Array.from(identities).some((identity) => assignedIdentitySet.has(identity));
  });
  const totalHours = task.timeLogs.reduce((sum, log) => sum + log.hours, 0);
  const estimatedHours = task.estimatedDays * 8;
  const timeProgressPercent = estimatedHours > 0
    ? Math.min((totalHours / estimatedHours) * 100, 100)
    : 0;
  const parentTasks = existingTasks.filter(t => (task.parentTaskIds || []).includes(t.id));
  const subtasks = existingTasks.filter(t => (task.subtaskIds || []).includes(t.id));
  const taskSprints = sprints.filter(s => (s.taskIds || []).includes(task.id));

  const handleStatusChange = (newStatus: TaskStatus) => {
    if (!canEditTaskParams) {
      return;
    }

    // Track status change
    if (task.status !== newStatus) {
      const changeLog: ChangeLog = {
        id: generateId('change', task.id),
        entityType: 'task',
        entityId: task.id,
        changeType: 'status',
        fieldName: 'status',
        oldValue: task.status,
        newValue: newStatus,
        changedBy: currentUser.id,
        changedAt: new Date().toISOString(),
      };
      const updates: Partial<Task> = {
        status: newStatus,
        changeLogs: [...(task.changeLogs || []), changeLog],
      };
      // Clear duplicate / reason metadata when leaving those statuses
      if (newStatus !== 'Duplicate') {
        updates.duplicateOfTaskId = undefined;
      }
      if (newStatus !== 'Other' && newStatus !== 'Duplicate') {
        updates.statusReason = undefined;
      }
      onUpdate(task.id, updates);
    } else {
      onUpdate(task.id, { status: newStatus });
    }
  };

  const handleSetDuplicateOf = (duplicateOfTaskId: string) => {
    if (!canEditTaskParams) return;
    onUpdate(task.id, { duplicateOfTaskId: duplicateOfTaskId || undefined });
  };

  const handleSetStatusReason = (statusReason: string) => {
    if (!canEditTaskParams) return;
    onUpdate(task.id, { statusReason });
  };

  const datesDirty =
    (canEditTaskParams && (
      startDate !== (task.startDate || '') ||
      endDate !== (task.targetEndDate || '') ||
      (targetDate !== (task.targetCompletionDate || ''))
    )) ||
    (canSetNextUpdateDate && String(nextUpdateDate || '').trim() !== (task.nextUpdateDate || ''));

  const handleSaveDates = () => {
    const updates: Partial<Task> = {};
    const changeLogs: ChangeLog[] = [...(task.changeLogs || [])];
    const now = new Date().toISOString();

    if (canEditTaskParams) {
      if (startDate !== (task.startDate || '')) {
        changeLogs.push({
          id: generateId('change', task.id),
          entityType: 'task',
          entityId: task.id,
          changeType: 'startDate',
          fieldName: 'startDate',
          oldValue: task.startDate || 'Not set',
          newValue: startDate || 'Cleared',
          changedBy: currentUser.id,
          changedAt: now,
        });
        updates.startDate = startDate || undefined;
      }
      if (endDate !== (task.targetEndDate || '')) {
        changeLogs.push({
          id: generateId('change', task.id),
          entityType: 'task',
          entityId: task.id,
          changeType: 'targetEndDate',
          fieldName: 'targetEndDate',
          oldValue: task.targetEndDate || 'Not set',
          newValue: endDate || 'Cleared',
          changedBy: currentUser.id,
          changedAt: now,
        });
        updates.targetEndDate = endDate || undefined;
      }
      if (targetDate !== (task.targetCompletionDate || '')) {
        changeLogs.push({
          id: generateId('change', task.id),
          entityType: 'task',
          entityId: task.id,
          changeType: 'targetDate',
          fieldName: 'targetCompletionDate',
          oldValue: task.targetCompletionDate || 'Not set',
          newValue: targetDate || 'Cleared',
          changedBy: currentUser.id,
          changedAt: now,
        });
        updates.targetCompletionDate = targetDate || undefined;
      }
    }

    if (canSetNextUpdateDate) {
      const normalizedNextUpdateDate = String(nextUpdateDate || '').trim();
      if (normalizedNextUpdateDate !== (task.nextUpdateDate || '')) {
        changeLogs.push({
          id: generateId('change', task.id),
          entityType: 'task',
          entityId: task.id,
          changeType: 'targetDate',
          fieldName: 'nextUpdateDate',
          oldValue: task.nextUpdateDate || 'Not set',
          newValue: normalizedNextUpdateDate || 'Cleared',
          changedBy: currentUser.id,
          changedAt: now,
        });
        updates.nextUpdateDate = normalizedNextUpdateDate || undefined;
      }
    }

    if (Object.keys(updates).length === 0) return;
    onUpdate(task.id, { ...updates, changeLogs });
  };

  const handleAddAssignee = () => {
    if (!canEditTaskParams) {
      return;
    }

    if (selectedAssignee && !(task.assigneeIds || []).includes(selectedAssignee)) {
      const user = assignableUsers.find(u => u.id === selectedAssignee);

      if (!user) {
        setSelectedAssignee('');
        return;
      }

      const currentAssigneeIds = task.assigneeIds || [];
      const changeLog: ChangeLog = {
        id: generateId('change', task.id),
        entityType: 'task',
        entityId: task.id,
        changeType: 'assignee',
        fieldName: 'assigneeIds',
        oldValue: currentAssigneeIds.join(', ') || 'None',
        newValue: [...currentAssigneeIds, selectedAssignee].join(', '),
        changedBy: currentUser.id,
        changedAt: new Date().toISOString(),
      };
      onUpdate(task.id, {
        assigneeIds: [...currentAssigneeIds, selectedAssignee],
        changeLogs: [...(task.changeLogs || []), changeLog],
      });
      setSelectedAssignee('');
    }
  };

  const handleRemoveAssignee = (userId: string) => {
    if (!canEditTaskParams) {
      return;
    }

    const targetUser = users.find((user) => user.id === userId);
    const targetIdentities = targetUser ? userIdentitySet(targetUser) : buildIdentitySet(userId);

    const updatedAssigneeIds = (task.assigneeIds || []).filter((assigneeId) => {
      const assigneeIdentities = buildIdentitySet(assigneeId);
      return !Array.from(assigneeIdentities).some((identity) => targetIdentities.has(identity));
    });

    const changeLog: ChangeLog = {
      id: generateId('change', task.id),
      entityType: 'task',
      entityId: task.id,
      changeType: 'assignee',
      fieldName: 'assigneeIds',
      oldValue: (task.assigneeIds || []).join(', '),
      newValue: updatedAssigneeIds.join(', ') || 'None',
      changedBy: currentUser.id,
      changedAt: new Date().toISOString(),
    };
    onUpdate(task.id, {
      assigneeIds: updatedAssigneeIds,
      changeLogs: [...(task.changeLogs || []), changeLog],
    });
  };

  const handleRateTask = (rating: number) => {
    if (!canEditTaskParams) {
      return;
    }

    onUpdate(task.id, {
      rating,
      ratedBy: currentUser.id,
      ratedAt: new Date().toISOString(),
    });
  };

  const handleSaveCostItemLinks = () => {
    if (!canEditTaskParams) {
      return;
    }

    const previousIds = task.costItemIds || [];
    const nextIds = Array.from(new Set(selectedCostItemIds));

    const sameLength = previousIds.length === nextIds.length;
    const sameValues = sameLength && previousIds.every((id) => nextIds.includes(id));
    if (sameValues) {
      return;
    }

    const lookupName = (id: string) => {
      const match = projectCostItems.find((item) => item.id === id);
      return match ? `[${ensureCostItemCode(match)}] ${match.description} (${match.category})` : id;
    };

    const changeLog: ChangeLog = {
      id: generateId('change', task.id),
      entityType: 'task',
      entityId: task.id,
      changeType: 'cost',
      fieldName: 'costItemIds',
      oldValue: previousIds.length > 0 ? previousIds.map(lookupName).join(', ') : 'None',
      newValue: nextIds.length > 0 ? nextIds.map(lookupName).join(', ') : 'None',
      changedBy: currentUser.id,
      changedAt: new Date().toISOString(),
    };

    onUpdate(task.id, {
      costItemIds: nextIds,
      changeLogs: [...(task.changeLogs || []), changeLog],
    });
  };

  const handleSaveDescription = () => {
    if (!canEditTaskParams) {
      return;
    }

    const nextDescription = descriptionDraft;
    if (nextDescription === (task.description || '')) {
      return;
    }

    const changeLog: ChangeLog = {
      id: generateId('change', task.id),
      entityType: 'task',
      entityId: task.id,
      changeType: 'description',
      fieldName: 'description',
      oldValue: task.description || 'Not set',
      newValue: nextDescription || 'Cleared',
      changedBy: currentUser.id,
      changedAt: new Date().toISOString(),
    };

    onUpdate(task.id, {
      description: nextDescription,
      changeLogs: [...(task.changeLogs || []), changeLog],
    });
  };

  const handleAddStandupNote = () => {
    if (!standupNote.trim()) return;
    
    const newNote = {
      id: generateId('note', task.id),
      authorId: currentUser.id,
      content: standupNote,
      createdAt: new Date().toISOString(),
      taskId: task.id,
    };

    const shouldClearNextUpdateDate = isCurrentUserAssignee && Boolean(task.nextUpdateDate);
    const nextUpdates: any = {
      standupNotes: [...task.standupNotes, newNote],
    };

    if (shouldClearNextUpdateDate) {
      const changeLog = {
        id: generateId('change', task.id),
        entityType: 'task',
        entityId: task.id,
        changeType: 'targetDate',
        fieldName: 'nextUpdateDate',
        oldValue: task.nextUpdateDate || 'Not set',
        newValue: 'Cleared by assignee update',
        changedBy: currentUser.id,
        changedAt: new Date().toISOString(),
      };

      nextUpdates.nextUpdateDate = undefined;
      nextUpdates.changeLogs = [...(task.changeLogs || []), changeLog as any];
      setNextUpdateDate('');
    }

    onUpdate(task.id, nextUpdates);
    setStandupNote('');
  };

  const handleAddTimeLog = () => {
    if (!timeLog.hours || parseFloat(timeLog.hours) <= 0) return;
    
    const newLog: TimeLog = {
      id: generateId('log', task.id),
      taskId: task.id,
      userId: currentUser.id,
      hours: parseFloat(timeLog.hours),
      description: timeLog.description,
      date: new Date().toISOString(),
    };

    onUpdate(task.id, {
      timeLogs: [...task.timeLogs, newLog],
    });
    setTimeLog({ hours: '', description: '' });
  };

  const handleAddVerificationLink = () => {
    if (!verificationLink.trim()) return;
    
    onUpdate(task.id, {
      verificationLinks: [...task.verificationLinks, verificationLink],
    });
    setVerificationLink('');
  };

  const handleAddAttachment = () => {
    if (!attachmentUrl.trim() || !attachmentName.trim()) return;
    
    const newAttachment = {
      id: generateId('attachment', task.id),
      name: attachmentName,
      url: attachmentUrl,
      type: attachmentName.split('.').pop() || 'file',
      uploadedAt: new Date().toISOString(),
      uploadedBy: currentUser.id,
    };

    onUpdate(task.id, {
      attachments: [...task.attachments, newAttachment],
    });
    setAttachmentUrl('');
    setAttachmentName('');
  };

  const typeColors = {
    'Bug': 'bg-destructive/10 text-destructive',
    'Feature': 'bg-primary/10 text-primary',
    'Improvement': 'bg-chart-4/10 text-chart-4',
  };

  const statusColors: Record<TaskStatus, string> = {
    'Not Started': 'bg-muted text-muted-foreground',
    'Active': 'bg-primary/10 text-primary',
    'In Progress': 'bg-chart-4/10 text-chart-4',
    'Code Complete': 'bg-chart-4/10 text-chart-4',
    'In Release': 'bg-chart-1/10 text-chart-1',
    'Paused': 'bg-chart-5/10 text-chart-5',
    'Completed': 'bg-chart-2/10 text-chart-2',
    'Deferred': 'bg-chart-5/10 text-chart-5',
    'Not Required': 'bg-muted text-muted-foreground',
    'Blocked': 'bg-destructive/10 text-destructive',
    'Duplicate': 'bg-muted text-muted-foreground',
    'Other': 'bg-muted text-muted-foreground',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-card rounded-lg shadow-xl max-w-5xl w-full my-8">
        <div className="sticky top-0 bg-card border-border px-6 py-4 flex items-center justify-between rounded-t-lg z-10">
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded text-sm ${typeColors[task.type]}`}>
              {task.type}
            </span>
            <h2 className="text-2xl">{task.title}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-accent rounded-lg" title="Close task details">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Status Change */}
        <div className="px-6 py-4 bg-muted border-border">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">Status:</span>
            <span className="text-xs text-muted-foreground">Role: {(projectMemberRole || currentUser.role).replace(/_/g, ' ')}</span>
            {([
              'Active',
              'In Progress',
              'Code Complete',
              'In Release',
              'Completed',
              'Blocked',
              'Duplicate',
              'Other',
              'Deferred',
              'Not Required',
            ] as TaskStatus[]).map((status) => (
              <button
                key={status}
                onClick={() => handleStatusChange(status)}
                disabled={!canEditTaskParams}
                className={`px-3 py-1 rounded-full text-sm transition-all ${
                  task.status === status
                    ? statusColors[status]
                    : 'bg-muted text-muted-foreground hover:bg-accent'
                } ${!canEditTaskParams ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {status}
              </button>
            ))}
          </div>
          {!canEditTaskParams && (
            <p className="text-xs text-chart-5 mt-2">
              You have stakeholder read-only access for task parameters. You can still add notes, comments, and logs.
            </p>
          )}

          {/* Duplicate: pick the canonical task this duplicates */}
          {task.status === 'Duplicate' && (
            <div className="mt-3 grid gap-2 sm:grid-cols-[auto_1fr_1fr] sm:items-center">
              <span className="text-sm text-muted-foreground">Duplicate of</span>
              <select
                aria-label="Duplicate of task"
                value={task.duplicateOfTaskId || ''}
                onChange={(e) => handleSetDuplicateOf(e.target.value)}
                disabled={!canEditTaskParams}
                className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
              >
                <option value="">— Select a task —</option>
                {existingTasks
                  .filter((t) => t.id !== task.id && t.projectId === task.projectId)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {(t.idLabel || t.id?.slice(-6))} — {t.title}
                    </option>
                  ))}
              </select>
              <input
                type="text"
                aria-label="Duplicate notes"
                placeholder="Optional note (e.g., merged into …)"
                value={task.statusReason || ''}
                onChange={(e) => handleSetStatusReason(e.target.value)}
                disabled={!canEditTaskParams}
                className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
              />
            </div>
          )}

          {/* Other: require an explanation */}
          {task.status === 'Other' && (
            <div className="mt-3">
              <label className="block text-sm text-muted-foreground mb-1">
                Explanation <span className="text-destructive">*</span>
              </label>
              <textarea
                aria-label="Status explanation"
                value={task.statusReason || ''}
                onChange={(e) => handleSetStatusReason(e.target.value)}
                disabled={!canEditTaskParams}
                rows={2}
                placeholder="Describe the current state of this task…"
                className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
              />
              {!task.statusReason?.trim() && (
                <p className="text-xs text-destructive mt-1">An explanation is required when status is &ldquo;Other&rdquo;.</p>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="border-border">
          <div className="flex overflow-x-auto px-6">
            {[
              { id: 'details', label: 'Details', icon: LinkIcon },
              { id: 'comments', label: 'Comments', icon: MessageSquare },
              { id: 'standup', label: 'Standup', icon: MessageSquare },
              { id: 'time', label: 'Time Log', icon: Clock },
              { id: 'attachments', label: 'Attachments', icon: Paperclip },
              { id: 'changes', label: 'Change Log', icon: History },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-ring text-ring'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6 max-h-[calc(100vh-400px)] overflow-y-auto">
          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Description */}
              <div>
                <h3 className="text-lg mb-2">Description</h3>
                {canEditTaskParams ? (
                  <RichDescriptionEditor
                    value={descriptionDraft}
                    onChange={setDescriptionDraft}
                    rows={5}
                    showPreview={true}
                    onSave={handleSaveDescription}
                    saveLabel="Save Description"
                    suggestionContext={{
                      title: task.title,
                      type: task.type,
                    }}
                  />
                ) : task.description ? (
                  <MarkdownDescription content={task.description} className="text-foreground" />
                ) : (
                  <p className="text-muted-foreground italic">No description provided</p>
                )}
              </div>

              {/* Task Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm text-muted-foreground mb-1">Task ID</h4>
                  <p className="font-mono text-sm">{task.id}</p>
                </div>
                {getTaskOrder(task) !== null && (
                <div>
                  <h4 className="text-sm text-muted-foreground mb-1">Order #</h4>
                  <p className="font-mono text-sm">#{getTaskOrder(task)}</p>
                </div>
                )}
                <div>
                  <h4 className="text-sm text-muted-foreground mb-1">Priority</h4>
                  <p className="capitalize">{task.priority}</p>
                </div>
                <div>
                  <h4 className="text-sm text-muted-foreground mb-1">Estimated Effort</h4>
                  <p>{task.estimatedDays} days</p>
                </div>
                <div>
                  <h4 className="text-sm text-muted-foreground mb-1">Total Hours Logged</h4>
                  <p>{totalHours} hours</p>
                </div>
                <div>
                  <h4 className="text-sm text-muted-foreground mb-1">Customer Visible</h4>
                  <p>{task.customerVisible ? 'Yes' : 'No'}</p>
                </div>
              </div>

              {/* Assignees */}
              <div>
                <h4 className="text-sm text-muted-foreground mb-2">Assignees</h4>
                {assignees.length === 0 ? (
                  <p className="text-muted-foreground italic">No assignees</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {assignees.map((user) => (
                      <div key={user.id} className="flex items-center gap-2 px-3 py-1 bg-muted rounded-full">
                        <UserIcon className="w-4 h-4" />
                        <span>{user.name}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <select
                    title="Select assignee"
                    value={selectedAssignee}
                    onChange={(e) => setSelectedAssignee(e.target.value)}
                    className="border border-input rounded-lg text-sm px-3 py-2 bg-background text-foreground"
                    disabled={!canEditTaskParams}
                  >
                    <option value="">Add Assignee</option>
                    {availableAssignees.map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleAddAssignee}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 text-sm"
                    disabled={!canEditTaskParams}
                  >
                    Add
                  </button>
                </div>
                {assignableUsers.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    No project team members are available for assignment.
                  </p>
                )}
              </div>

              {/* Parent Tasks */}
              {parentTasks.length > 0 && (
                <div>
                  <h4 className="text-sm text-muted-foreground mb-2">Parent Tasks</h4>
                  <div className="space-y-2">
                    {parentTasks.map((parent) => (
                      <div key={parent.id} className="p-3 bg-muted rounded-lg">
                        <p className="text-sm">{parent.title}</p>
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs ${statusColors[parent.status]}`}>
                          {parent.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Subtasks */}
              {subtasks.length > 0 && (
                <div>
                  <h4 className="text-sm text-muted-foreground mb-2">Subtasks</h4>
                  <div className="space-y-2">
                    {subtasks.map((subtask) => (
                      <div key={subtask.id} className="p-3 bg-muted rounded-lg">
                        <p className="text-sm">{subtask.title}</p>
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs ${statusColors[subtask.status]}`}>
                          {subtask.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sprints */}
              {taskSprints.length > 0 && (
                <div>
                  <h4 className="text-sm text-muted-foreground mb-2">Sprints</h4>
                  <div className="flex flex-wrap gap-2">
                    {taskSprints.map((sprint) => (
                      <span key={sprint.id} className="px-3 py-1 bg-chart-4/10 text-chart-4 rounded-full text-sm">
                        {sprint.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* External Links */}
              {task.externalLinks.length > 0 && (
                <div>
                  <h4 className="text-sm text-muted-foreground mb-2">External Links</h4>
                  <div className="space-y-2">
                    {task.externalLinks.map((link, index) => (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 border border-border rounded-lg hover:bg-accent"
                      >
                        <ExternalLink className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{link.type}</span>
                        <span className="text-sm text-muted-foreground truncate flex-1">{link.url}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Verification Links (for bugs) */}
              {task.type === 'Bug' && (
                <div>
                  <h4 className="text-sm text-muted-foreground mb-2">Verification Links</h4>
                  <div className="space-y-2 mb-3">
                    {task.verificationLinks.map((link, index) => (
                      <a
                        key={index}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-2 text-sm text-ring hover:underline truncate"
                      >
                        {link}
                      </a>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      placeholder="Add verification link (proof of fix)"
                      value={verificationLink}
                      onChange={(e) => setVerificationLink(e.target.value)}
                      className="flex-1 border border-input rounded-lg text-sm px-3 py-2 bg-background text-foreground"
                    />
                    <button
                      onClick={handleAddVerificationLink}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 text-sm"
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}

              {/* Customer Info (for bugs) */}
              {task.type === 'Bug' && task.reportedBy && (
                <div>
                  <h4 className="text-sm text-muted-foreground mb-1">Reported By</h4>
                  <p>{task.reportedBy}</p>
                </div>
              )}

              {/* Dates */}
              <div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <h4 className="text-sm text-muted-foreground mb-2">Start Date</h4>
                    <input
                      title="Task start date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full border border-input rounded-lg text-sm px-3 py-2 bg-background text-foreground"
                      disabled={!canEditTaskParams}
                    />
                  </div>
                  <div>
                    <h4 className="text-sm text-muted-foreground mb-2">End Date</h4>
                    <input
                      title="Task end date"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full border border-input rounded-lg text-sm px-3 py-2 bg-background text-foreground"
                      disabled={!canEditTaskParams}
                    />
                  </div>
                  <div>
                    <h4 className="text-sm text-muted-foreground mb-2">Target Completion</h4>
                    <input
                      title="Task target completion date"
                      type="date"
                      value={targetDate}
                      onChange={(e) => setTargetDate(e.target.value)}
                      className="w-full border border-input rounded-lg text-sm px-3 py-2 bg-background text-foreground"
                      disabled={!canEditTaskParams}
                    />
                  </div>
                  <div>
                    <h4 className="text-sm text-muted-foreground mb-2">Next Update</h4>
                    <input
                      title="Task next update date"
                      type="date"
                      value={nextUpdateDate}
                      onChange={(e) => setNextUpdateDate(e.target.value)}
                      className="w-full border border-input rounded-lg text-sm px-3 py-2 bg-background text-foreground"
                      disabled={!canSetNextUpdateDate}
                    />
                  </div>
                </div>
                {(canEditTaskParams || canSetNextUpdateDate) && (
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={handleSaveDates}
                      disabled={!datesDirty}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Save Dates
                    </button>
                  </div>
                )}
              </div>

              {/* Linked Cost Items */}
              {canViewFinance && projectCostItems.length > 0 && (
                <div>
                  <h4 className="text-sm text-muted-foreground mb-2">Linked Cost Items</h4>
                  <div className="space-y-2 rounded-lg border border-border p-3 bg-muted">
                    {projectCostItems.map((item) => (
                      <label key={item.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={selectedCostItemIds.includes(item.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCostItemIds([...selectedCostItemIds, item.id]);
                            } else {
                              setSelectedCostItemIds(selectedCostItemIds.filter((id) => id !== item.id));
                            }
                          }}
                          disabled={!canEditTaskParams}
                          className="w-4 h-4"
                        />
                        <span>
                          [{ensureCostItemCode(item)}] {item.description} ({item.category}) - ${item.amount.toLocaleString()}
                        </span>
                      </label>
                    ))}
                    <div className="pt-2">
                      <button
                        onClick={handleSaveCostItemLinks}
                        disabled={!canEditTaskParams}
                        className="px-3 py-1.5 bg-primary text-primary-foreground rounded text-sm hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        Apply Linked Cost Items
                      </button>
                    </div>
                    {linkedCostItems.length === 0 && (
                      <p className="text-xs text-muted-foreground">No cost items linked yet.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Rating */}
              <div>
                <h4 className="text-sm text-muted-foreground mb-2">Rate Task Quality</h4>
                {task.rating ? (
                  <div className="mb-3 p-3 bg-muted border border-border rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Current Rating:</span>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map(star => (
                          <Star 
                            key={star}
                            className={`w-5 h-5 ${
                              star <= (task.rating || 0) 
                                ? 'fill-current text-chart-5' 
                                : 'text-muted-foreground'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-muted-foreground">({task.rating}/5)</span>
                    </div>
                  </div>
                ) : null}
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map(rating => (
                    <button
                      key={rating}
                      onClick={() => handleRateTask(rating)}
                      className={`p-2 border border-input rounded-lg hover:bg-accent transition-all ${
                        task.rating === rating ? 'bg-muted border-chart-5/40' : ''
                      }`}
                      title={`Rate ${rating} stars`}
                      disabled={!canEditTaskParams}
                    >
                      <Star 
                        className={`w-5 h-5 ${
                          rating <= (task.rating || 0)
                            ? 'fill-current text-chart-5'
                            : 'text-muted-foreground'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Cost Per Hour */}
              {canViewFinance && (
                <div>
                  <h4 className="text-sm text-muted-foreground mb-3">Cost Per Hour & Budget</h4>
                  <CostPerHourDisplay 
                    task={task}
                    epic={epic}
                    project={project}
                    currentUser={currentUser}
                    onUpdateTask={(updates) => onUpdate(task.id, updates)}
                    onUpdateEpic={epic && onUpdateEpic ? (updates) => onUpdateEpic(epic.id, updates) : undefined}
                    onUpdateProject={project && onUpdateProject ? (updates) => onUpdateProject(project.id, updates) : undefined}
                  />
                </div>
              )}

              {/* Cost Summary */}
              {canViewFinance && (task.estimatedHours || totalHours > 0) && (
                <div className="bg-muted rounded-lg p-4 border border-border">
                  <h4 className="text-sm font-semibold text-foreground mb-3">Cost Summary</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Estimated Cost:</span>
                      <span className="text-lg font-semibold text-foreground">
                        {formatCurrency(calculateEstimatedCost(task, epic, project))}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Actual Cost:</span>
                      <span className="text-lg font-semibold text-foreground">
                        {formatCurrency(calculateActualCost(task, epic, project))}
                      </span>
                    </div>
                    {totalHours > 0 && (
                      <div className="flex justify-between items-center pt-2 border-t border-border">
                        <span className="text-sm text-muted-foreground">Variance:</span>
                        <span className={`text-lg font-semibold ${
                          calculateActualCost(task, epic, project) > calculateEstimatedCost(task, epic, project)
                            ? 'text-destructive'
                            : 'text-chart-2'
                        }`}>
                          {formatCurrency(calculateActualCost(task, epic, project) - calculateEstimatedCost(task, epic, project))}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Comments Tab */}
          {activeTab === 'comments' && (
            <div className="p-6">
              <CommentSection
                comments={localComments}
                currentUserId={currentUser.id}
                currentUserName={currentUser.name || currentUser.email || 'User'}
                onAddComment={(content, parentCommentId) => {
                  const newComment: Comment = {
                    id: generateId('cmt'),
                    content,
                    authorId: currentUser.id,
                    authorName: currentUser.name || currentUser.email || 'User',
                    createdAt: new Date().toISOString(),
                    parentCommentId,
                  };
                  const updated = [...localComments, newComment];
                  setLocalComments(updated);
                  onUpdate(task.id, { comments: updated, updatedAt: new Date().toISOString() });
                }}
                onEditComment={(commentId, newContent) => {
                  const updated = localComments.map((c) =>
                    c.id === commentId ? { ...c, content: newContent, edited: true } : c
                  );
                  setLocalComments(updated);
                  onUpdate(task.id, { comments: updated, updatedAt: new Date().toISOString() });
                }}
                onDeleteComment={(commentId) => {
                  const updated = localComments.filter((c) => c.id !== commentId);
                  setLocalComments(updated);
                  onUpdate(task.id, { comments: updated, updatedAt: new Date().toISOString() });
                }}
                placeholder="Add a comment for this task..."
              />
            </div>
          )}

          {/* Standup Notes Tab */}
          {activeTab === 'standup' && (
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-lg border border-border">
                <h3 className="mb-3">Add Standup Note</h3>
                {task.nextUpdateDate && (
                  <p className="text-xs text-ring mb-3">
                    Next update due on {formatDate(task.nextUpdateDate)}.
                    {isCurrentUserAssignee ? ' Submitting your update clears this reminder.' : ''}
                  </p>
                )}
                <textarea
                  value={standupNote}
                  onChange={(e) => setStandupNote(e.target.value)}
                  placeholder="What did you work on? Any blockers?"
                  className="w-full border border-input rounded-lg mb-3 px-4 py-2 bg-background text-foreground"
                  rows={3}
                />
                <button
                  onClick={handleAddStandupNote}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                >
                  Add Note
                </button>
              </div>

              <div className="space-y-3">
                {task.standupNotes.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No standup notes yet</p>
                ) : (
                  task.standupNotes.map((note) => {
                    const author = users.find(u => u.id === note.authorId);
                    return (
                      <div key={note.id} className="p-4 bg-card border border-border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span>{author?.name || 'Unknown'}</span>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(note.createdAt)} {formatTime(note.createdAt)}
                          </span>
                        </div>
                        <p className="text-foreground whitespace-pre-wrap">{note.content}</p>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Time Tracking Tab */}
          {activeTab === 'time' && (
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-lg border border-border">
                <h3 className="mb-3">Log Time</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <input
                    type="number"
                    placeholder="Hours worked"
                    value={timeLog.hours}
                    onChange={(e) => setTimeLog({ ...timeLog, hours: e.target.value })}
                    className="border border-input rounded-lg px-4 py-2 bg-background text-foreground"
                    min="0"
                    step="0.5"
                  />
                  <input
                    type="text"
                    placeholder="What did you work on?"
                    value={timeLog.description}
                    onChange={(e) => setTimeLog({ ...timeLog, description: e.target.value })}
                    className="border border-input rounded-lg px-4 py-2 bg-background text-foreground"
                  />
                </div>
                <button
                  onClick={handleAddTimeLog}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                >
                  Log Time
                </button>
              </div>

              <div className="bg-card p-4 rounded-lg border border-border">
                <h4 className="mb-2">Total Hours: {totalHours}h / {task.estimatedDays * 8}h estimated</h4>
                <progress
                  value={Math.max(0, timeProgressPercent)}
                  max={100}
                  className="w-full h-2 overflow-hidden rounded-full [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:bg-ring [&::-moz-progress-bar]:bg-ring"
                />
              </div>

              <div className="space-y-2">
                {task.timeLogs.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No time logs yet</p>
                ) : (
                  task.timeLogs.map((log) => {
                    const user = users.find(u => u.id === log.userId);
                    return (
                      <div key={log.id} className="p-3 bg-card border border-border rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span>{user?.name || 'Unknown'}</span>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(log.date)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-foreground">{log.description}</p>
                          <span className="px-2 py-1 bg-muted text-foreground rounded text-sm ml-2">
                            {log.hours}h
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Attachments Tab */}
          {activeTab === 'attachments' && (
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-lg border border-border">
                <h3 className="mb-3">Add Attachment</h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="File name"
                    value={attachmentName}
                    onChange={(e) => setAttachmentName(e.target.value)}
                    className="w-full border border-input rounded-lg px-4 py-2 bg-background text-foreground"
                  />
                  <input
                    type="url"
                    placeholder="File URL (or placeholder for file upload)"
                    value={attachmentUrl}
                    onChange={(e) => setAttachmentUrl(e.target.value)}
                    className="w-full border border-input rounded-lg px-4 py-2 bg-background text-foreground"
                  />
                  <button
                    onClick={handleAddAttachment}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                  >
                    Add Attachment
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {task.attachments.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No attachments yet</p>
                ) : (
                  task.attachments.map((attachment) => {
                    const uploader = users.find(u => u.id === attachment.uploadedBy);
                    return (
                      <div key={attachment.id} className="p-3 bg-card border border-border rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Paperclip className="w-4 h-4 text-muted-foreground" />
                            <span>{attachment.name}</span>
                          </div>
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-ring hover:underline text-sm"
                          >
                            View
                          </a>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Uploaded by {uploader?.name || 'Unknown'} on {formatDate(attachment.uploadedAt)}
                        </p>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Change Log Tab */}
          {activeTab === 'changes' && (
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-lg border border-border">
                <h3 className="mb-3">Change Log</h3>
                <div className="space-y-2">
                  {visibleChangeLogs.length > 0 ? visibleChangeLogs.map((log) => {
                    const user = users.find(u => u.id === log.changedBy);
                    return (
                      <div key={log.id} className="p-3 bg-card border border-border rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span>{user?.name || 'Unknown'}</span>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(log.changedAt)} {formatTime(log.changedAt)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-foreground">{log.changeType}</p>
                          <span className="px-2 py-1 bg-muted text-foreground rounded text-sm ml-2">
                            {log.oldValue} → {log.newValue}
                          </span>
                        </div>
                      </div>
                    );
                  }) : <p className="text-center text-muted-foreground py-8">No visible changes</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}